import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BrandSuggestionResponse, ContextResponse } from '@woolworthsnz/trader-api';
import {
	DatalayerService,
	PromotionService,
	PromoImpressionArgs,
	SearchTarget,
	TrackImpressionDirective,
	FeatureService,
	TealiumEventEnum,
} from '@woolworthsnz/styleguide';
import { NgIf, NgFor } from '@angular/common';
import { TealiumUtagService } from '@woolworthsnz/analytics';

@Component({
	selector: 'product-brand-suggestion',
	template: `
		<ng-container *ngIf="brands?.length > 0">
			<section class="brand-suggestions-container">
				<div class="brand-suggestions-title-container">
					<h2 class="brand-suggestions-title" [attr.search-target]="isSearchTarget">{{ title }}</h2>
					<div class="sponsored-badge">Promoted</div>
				</div>
				<ng-container *ngFor="let brand of brands ? brands.slice(0, maxNumberOfBrands) : []; let i = index">
					<a
						[routerLink]="getRouterLink(brand)"
						(click)="handleBrandSuggestionClick(brand)"
						[cdxTrackImpression]="this.brandPromoImpressionArgs.get(i)"
					>
						<picture>
							<source media="(max-width: 767px)" [srcset]="brand.mobileImageUrl" />
							<source media="(min-width: 768px)" [srcset]="brand.imageUrl" />
							<img [src]="brand.mobileImageUrl" [alt]="brand.altText" />
						</picture>
					</a>
				</ng-container>
			</section>
		</ng-container>
	`,
	styleUrls: ['./brand-suggestion.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgFor, RouterLink, TrackImpressionDirective],
})
export class BrandSuggestionComponent implements OnInit {
	readonly maxNumberOfBrands: number = 8;
	brandPromoImpressionArgs: Map<number, PromoImpressionArgs> = new Map();

	@Input()
	public brands: BrandSuggestionResponse[];

	@Input() target: string;

	@Input() heading: string;

	isSearchTarget: boolean;

	private enableCartologyGA4EventTracking: boolean;
	private enableBrandSuggectionBugfix: boolean;
	private enableConsistencyAndPriorityForHomePageAndSearchBanners: boolean;

	get title(): string {
		return this.target === SearchTarget.Search ? 'Brands related to your search' : 'More ways to shop';
	}

	constructor(
		private router: Router,
		private datalayerService: DatalayerService,
		private promotionService: PromotionService,
		private featureService: FeatureService,
		private tealiumUtagService: TealiumUtagService
	) {}

	ngOnInit(): void {
		this.isSearchTarget = this.target === SearchTarget.Search;
		this.enableCartologyGA4EventTracking = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableCartologyGA4EventTracking
		);

		this.enableBrandSuggectionBugfix = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.NewRecipeABTest
		);

		this.enableConsistencyAndPriorityForHomePageAndSearchBanners = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableConsistencyAndPriorityForBanners
		);

		this.setupBrandPromoImpressions();
	}

	getRouterLink(brand: BrandSuggestionResponse): string {
		return this.enableBrandSuggectionBugfix && brand.linkUrl ? brand.linkUrl : '/';
	}

	setupBrandPromoImpressions(): void {
		if (!!this.brands && this.brands.length > 0) {
			for (let index = 0; index < this.brands.length; ++index) {
				const data: PromoImpressionArgs = {
					id: (this.brands[index].id || '').toString(),
					name: this.brands[index].name || '',
					position: `${index}`,
					creative: `${this.heading}`,
				};
				this.brandPromoImpressionArgs.set(index, data);
			}

			this.datalayerService.trackPromoImpression([...this.brandPromoImpressionArgs.values()]);
			if (this.enableCartologyGA4EventTracking === true) {
				this.brands.forEach((brand: BrandSuggestionResponse) => this.submitViewPromotionImpression(brand));
			}
		}
	}

	handleBrandSuggestionClick(brand: BrandSuggestionResponse): void {
		this.datalayerService.trackPromoClick({
			id: brand.id,
			name: brand.name,
			position: `${brand.positionIndex || ''}`,
			creative: `${this.heading}`,
		});

		if (this.enableCartologyGA4EventTracking === true) {
			const promotionId = this.enableConsistencyAndPriorityForHomePageAndSearchBanners
				? brand.id ?? brand.hoverText
				: brand.hoverText ?? brand.id;
			this.tealiumUtagService.link(
				{
					tealium_event: TealiumEventEnum.SelectPromotion,
					ecommerce: {
						creative_name: `${this.isSearchTarget === true ? 'Search' : 'Browse'} Brand Tile`,
						creative_slot: brand.positionIndex || 0,
						promotion_id: promotionId,
						promotion_name: brand.name,
					},
				},
				true
			);
		}

		this.promotionService.onBrandSuggestionClick({ linkUrl: brand.linkUrl, id: (brand.id || '').toString() });

		this.router.navigateByUrl(brand.linkUrl || '', { replaceUrl: false });
	}

	private submitViewPromotionImpression(brand: BrandSuggestionResponse): void {
		this.tealiumUtagService.view({
			event: TealiumEventEnum.ViewPromotion,
			tealium_event: TealiumEventEnum.ViewPromotion,
			ecommerce: {
				creative_name: `${this.isSearchTarget === true ? 'Search' : 'Browse'} Brand Tile`,
				creative_slot: brand.positionIndex || 0,
				promotion_id: brand.hoverText ?? brand.id,
				promotion_name: brand.name,
			},
		});
	}
}
